import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import { env } from 'utils/env';
import { useTranslation } from 'react-i18next';
import { ToastContainer } from '@totalsoft/rocket-ui';
import { Container, Content } from './AppStyle';
import Sidebar from './layout/sidebar/Sidebar';
import Header from './layout/header/Header';
import AppRoutes from 'routes/AppRoutes';
import GlobalLoadingIndicator from 'features/common/components/GlobalLoadingIndicator';
import GlobalConfirmDialog from 'features/common/components/GlobalConfirmDialog';
import Main from './layout/Main';
import { useMediaQuery, useTheme } from '@mui/material';
import { useServerErrorHandling, useToast } from 'hooks';
import { useOidc } from '@axa-fr/react-oidc';
import { useApolloClient } from '@apollo/client';
import { resetCache } from 'utils/cache/resetCache';
import { closeSocket } from 'apollo/client';
import GlobalSubscriptionArea from 'features/common/components/GlobalSubscriptionArea';
import { getOidcConfigName } from 'utils/functions';
import { useI18nextGlobalTextResolver } from '@ts-private/list-browser-i18next-extensions';

export default function App() {
  const addToast = useToast();
  const location = useLocation();
  const mainPanelRef = useRef();
  const { i18n, t } = useTranslation();
  const theme = useTheme();
  const overMd = useMediaQuery(theme.breakpoints.up('md'));
  const client = useApolloClient();
  const { logout } = useOidc(getOidcConfigName());
  useI18nextGlobalTextResolver();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMinimized, setDrawerMinimized] = useState(true);

  const handleDrawerMinimizeToggle = useCallback(() => {
    setDrawerMinimized(prev => !prev);
  }, []);

  const handleDrawerOpenToggle = useCallback(() => {
    setDrawerOpen(prev => !prev);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const changeLanguage = useCallback(
    lng => {
      i18n.changeLanguage(lng.target.value).then(() => {
        resetCache(client);
        closeSocket('Language changed');
      });
    },
    [i18n, client]
  );

  useIdleTimer({
    timeout: 1000 * env.REACT_APP_IDLE_TIMEOUT,
    onIdle: () => {
      console.log('user is idle');
    },
    onActive: _ => {
      logout();
    },
    debounce: 500,
    crossTab: true
  });

  useEffect(() => {
    if (overMd) setDrawerOpen(false);
  }, [overMd]);

  useEffect(() => {
    mainPanelRef.current.scrollTop = 0;
  }, [location.pathname]);

  useServerErrorHandling({
    global: true,
    effect: input => {
      const { message, correlationId } = input;
      addToast(t('General.CommandExecutionError', { message }), 'error', { toastId: 'commandExecutionErrorToast' }, correlationId);
    }
  });

  return (
    <Container>
      <Sidebar
        closeDrawer={handleCloseDrawer}
        changeLanguage={changeLanguage}
        drawerOpen={drawerOpen}
        drawerMinimized={drawerMinimized}
        withGradient={false}
      />
      <Content ref={mainPanelRef} drawerMinimized={drawerMinimized}>
        <Header
          drawerMinimized={drawerMinimized}
          handleDrawerMinimizeToggle={handleDrawerMinimizeToggle}
          handleDrawerOpenToggle={handleDrawerOpenToggle}
        />
        <Main>
          <AppRoutes />
        </Main>
      </Content>
      <GlobalSubscriptionArea />
      <GlobalLoadingIndicator />
      <GlobalConfirmDialog />
      <ToastContainer theme='colored' />
    </Container>
  );
}
